<script lang="ts">
  /* InfoTooltip — a small "(?)" help icon that toggles a popover with
     descriptive text on click (Task 6). Replaces the always-on grey
     subtitles that used to sit under page titles. Click outside or press
     Escape to dismiss. */
  import { _ } from "svelte-i18n";

  let { text, label = "" }: { text: string; label?: string } = $props();

  let open = $state(false);
  let wrap = $state<HTMLElement | null>(null);
  let pop = $state<HTMLElement | null>(null);
  /* Placement: above by default (opening downwards covered the rows the popup
     explains), flipped below when there is no room above — near the top of
     the viewport the popup used to be cut off (2026-08-14 feedback, photo).
     Also nudged left when it would run off the right edge.

     Positioned `fixed`, against the viewport, rather than absolutely inside
     `.info-wrap`: most of the icons sit in a table header, and every table on
     the site scrolls horizontally inside `overflow-x: auto`, which clips an
     absolutely-positioned child — the «Группа товара» popup came out as a
     dark sliver above the header row (2026-08-30 feedback, photo). A fixed
     box has no overflow ancestor to be cut by, so the coordinates are
     measured from the anchor here and refreshed while the page moves. */
  let below = $state(false);
  let left = $state(0);
  let top = $state(0);
  /* Where the arrow sits along the popup's own edge. Not a fixed 12px: the box
     is pulled back inside the viewport when it would run off an edge, and a
     fixed arrow then pointed at whatever happened to be under it instead of at
     the icon that opened it — which is what made the popup read as a panel
     dropped on top of the page rather than as this «?»'s answer (2026-09-03
     feedback, photo). Measured from the anchor, so it always points home. */
  let arrow = $state(12);
  /* The first frame of an opened popup has no measurements yet — without this
     it would flash in the viewport's top-left corner before being placed. */
  let placed = $state(false);
  /* Bumped on every scroll/resize while open, purely to re-run the placement
     effect against the anchor's new position. */
  let tick = $state(0);

  function place() {
    if (!open || !pop || !wrap) return;
    const wr = wrap.getBoundingClientRect();
    const pr = pop.getBoundingClientRect();
    // The sticky topbar covers the top of the page in its own stacking
    // context, so "no room above" starts below it, not at the viewport edge.
    const topbarH =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue("--topbar-h"),
      ) || 0;
    // Computed from the anchor + the popup's own size, so re-runs after the
    // state writes below converge instead of oscillating.
    below = wr.top - pr.height - 12 < topbarH;
    top = below ? wr.bottom + 8 : wr.top - pr.height - 8;
    // Left-aligned with the icon, pulled back inside the viewport when the
    // box would run off either edge.
    left = Math.max(8, Math.min(wr.left, window.innerWidth - 8 - pr.width));
    // And the arrow follows the icon's centre wherever the box ended up,
    // clamped so it stays on the box's own edge rather than off its corner.
    arrow = Math.max(10, Math.min(wr.left + wr.width / 2 - left, pr.width - 10));
    placed = true;
  }

  $effect(() => {
    if (!open || !pop || !wrap) {
      below = false;
      placed = false;
      return;
    }
    tick; // re-place on scroll / resize
    place();
  });

  /* Scroll is listened to in the capture phase: the icons sit inside tables
     that scroll on their own, and a scroll on an inner element never bubbles
     to the window. Only while the popup is open — no idle scroll handler. */
  $effect(() => {
    if (!open) return;
    const bump = () => (tick += 1);
    window.addEventListener("scroll", bump, true);
    window.addEventListener("resize", bump);
    return () => {
      window.removeEventListener("scroll", bump, true);
      window.removeEventListener("resize", bump);
    };
  });

  function toggle() {
    open = !open;
  }

  function onWindowClick(e: MouseEvent) {
    if (open && wrap && !wrap.contains(e.target as Node)) open = false;
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") open = false;
  }
</script>

<svelte:window onclick={onWindowClick} onkeydown={onKey} />

<span class="info-wrap" bind:this={wrap}>
  <button
    type="button"
    class="info-btn"
    class:active={open}
    aria-label={label || $_("common.moreInfo")}
    aria-expanded={open}
    onclick={toggle}
  >
    <span class="material-icons-outlined">help_outline</span>
  </button>
  {#if open}
    <span
      class="info-pop"
      class:info-pop--below={below}
      class:info-pop--placed={placed}
      style:left="{left}px"
      style:top="{top}px"
      style:--arrow-x="{arrow}px"
      role="tooltip"
      bind:this={pop}>{text}</span
    >
  {/if}
</span>

<style>
  .info-wrap { position: relative; display: inline-flex; vertical-align: middle; }
  /* Black, deliberately: the black «?» explains what the section is for,
     the blue «?» beside it (where a page has one) opens the how-to tips —
     the colour is the only thing telling the two apart (2026-08-14
     feedback). So this one must not turn blue, not even on hover. */
  .info-btn {
    display: inline-flex; align-items: center; justify-content: center;
    border: none; background: none; padding: 2px; margin: 0; cursor: pointer;
    color: var(--color-surface-darker, #303841); border-radius: 50%; line-height: 1;
    transition: background 0.15s;
  }
  .info-btn:hover, .info-btn.active { background: rgba(48,56,65,0.1); }
  .info-btn .material-icons-outlined { font-size: 18px; }
  /* Above the icon, not below: opening downwards covered the first table
     rows the popup was explaining. Sentence case regardless of context —
     table headers are text-transformed to caps, and a popup inheriting
     that reads like shouting (2026-08-13 feedback). */
  .info-pop {
    /* Fixed, not absolute: an absolutely-positioned popup is clipped by the
       nearest `overflow` ancestor, and nearly every icon on this site sits in
       a horizontally-scrolling table. `left`/`top` come from the anchor. */
    position: fixed; z-index: 1200;
    width: max-content; max-width: 320px;
    background: var(--color-surface-darker, #303841); color: #fff;
    padding: 10px 12px; border-radius: 8px; font-size: 12.5px; font-weight: 400;
    line-height: 1.45; box-shadow: 0 6px 20px rgba(0,0,0,0.25);
    white-space: normal;
    text-transform: none; letter-spacing: normal; text-align: left;
    visibility: hidden;
  }
  .info-pop--placed { visibility: visible; }
  .info-pop::before {
    content: ""; position: absolute; top: 100%;
    /* Set from the anchor's centre in `place()`; the fallback is the old
       fixed offset, for the frame before the first measurement. */
    left: calc(var(--arrow-x, 12px) - 6px);
    border: 6px solid transparent; border-top-color: var(--color-surface-darker, #303841);
  }
  /* Flipped: under the icon, arrow pointing up. */
  .info-pop--below::before {
    top: auto; bottom: 100%;
    border-top-color: transparent;
    border-bottom-color: var(--color-surface-darker, #303841);
  }
</style>
